import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardHeader,
  TableFooter,
  TablePagination,
  Typography,
} from '@mui/material'
import { DEFAULT_TIMEZONE, HeadCell } from 'components-ui'
import { i18WithDefault as t } from '@shared/locale'
import { useMemo, useState } from 'react'
import { styled } from '@mui/material/styles'
import { formatInTimeZone } from 'date-fns-tz'
import { useLocationContext } from '@providers/location'
import { VoucherClientDto } from 'service-api/src/types'
import { PackageCreditsTableRow, PackageCreditsTableType, RedeemedPackageCredits } from './types'

const CREDIT_TYPE = 'displayName'
const SOURCE = 'sourceDisplayName'
const DATE_ADDED = 'createdAt'
const DATE_EXPIRED = 'expiredAt'
const DATE_REDEEMED = 'usedAt'
const ORDER_ID = 'orderId'

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50]

const StyledTablePagination = styled(TablePagination)({
  '& .MuiTablePagination-toolbar': {
    paddingLeft: 0,
  },
  '& .MuiInputBase-root': {
    fontSize: 14,
  },
  '& .MuiSelect-select': {
    paddingBottom: 0,
  },
})

export const PackageCreditsTable = ({
  type,
  data,
  renderCell,
  orderBy = DATE_ADDED,
  order = 'asc',
}: {
  type: PackageCreditsTableType
  data: VoucherClientDto[]
  renderCell?: {
    [key: string]: (date: string, row: PackageCreditsTableRow | RedeemedPackageCredits) => React.ReactNode
  }
  orderBy?: keyof PackageCreditsTableRow
  order?: 'asc' | 'desc'
}) => {
  const { location } = useLocationContext()
  const timezone = location?.timezone

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[1])

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage)
  }

  const headers: { [key: string]: HeadCell } = useMemo(
    () => ({
      [SOURCE]: {
        id: SOURCE,
        label: `${t('global.label.credit')} ${t('profile.packages.label.source')}`,
        width: '30%',
      },
      [CREDIT_TYPE]: {
        id: CREDIT_TYPE,
        label: `${t('global.label.credit')} ${t('profile.packages.label.type')}`,
        width: '30%',
      },
      [DATE_ADDED]: { id: DATE_ADDED, label: t('profile.packages.label.addedOn'), width: '20%' },
      [DATE_EXPIRED]: {
        id: DATE_EXPIRED,
        label: type === 'EXPIRED' ? t('profile.packages.label.expiredOn') : t('profile.packages.label.expiresOn'),
        width: '20%',
      },
      [DATE_REDEEMED]: { id: DATE_REDEEMED, label: t('profile.packages.label.redeemedOn'), width: '20%' },
    }),
    [type]
  )

  const tableConfig = useMemo(() => {
    switch (type) {
      case 'EXPIRED':
        return {
          title: `${t('profile.packages.label.expired')} ${t('global.label.credits')}`,
          headers: [headers[SOURCE], headers[CREDIT_TYPE], headers[DATE_ADDED], headers[DATE_EXPIRED]],
        }
      case 'AVAILABLE':
        return {
          title: `${t('profile.packages.label.available')} ${t('global.label.credits')}`,
          headers: [headers[SOURCE], headers[CREDIT_TYPE], headers[DATE_ADDED], headers[DATE_EXPIRED]],
        }
      case 'REDEEMED':
        return {
          title: `${t('profile.packages.label.redeemed')} ${t('global.label.credits')}`,
          headers: [headers[SOURCE], headers[CREDIT_TYPE], headers[DATE_ADDED], headers[DATE_REDEEMED]],
        }
      default:
        return {
          title: '',
          headers: [],
        }
    }
  }, [headers, type])

  const rows = useMemo((): PackageCreditsTableRow[] => {
    const tz = timezone || DEFAULT_TIMEZONE
    const dateFormat = 'MMM dd, yyyy'
    const sortedData =
      data?.sort((a, b) => {
        if (!a[orderBy] && !b[orderBy]) {
          return 0
        }

        if (!a[orderBy]) {
          return 1
        }

        if (!b[orderBy]) {
          return -1
        }
        const comparison = new Date(a[orderBy]).getTime() - new Date(b[orderBy]).getTime()
        return order === 'desc' ? -comparison : comparison
      }) || []

    const format = (date: string | number | Date | null | undefined) =>
      date ? formatInTimeZone(new Date(date), tz, dateFormat) : null

    return (
      sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => ({
        [SOURCE]: row.sourceDisplayName || '',
        [CREDIT_TYPE]: row.displayName || '',
        [DATE_ADDED]: format(row.createdAt) || '',
        [DATE_EXPIRED]: format(row.expiredAt) || '',
        [DATE_REDEEMED]: format(row.usedAt) || '',
        [ORDER_ID]: row.transactions?.[0]?.invoice?.orderId || row.transactions?.[0]?.invoice?.order?.id || '',
      })) || []
    )
  }, [data, page, rowsPerPage, timezone, orderBy, order])

  return (
    <Card sx={{ py: 1 }}>
      <CardHeader title={tableConfig.title} sx={{ px: 2, py: 1 }} />
      <TableContainer sx={{ pl: 2, pr: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <>
            <TableHead>
              <TableRow>
                {tableConfig.headers.map((headCell) => (
                  <TableCell key={headCell.id} width={headCell.width}>
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row, index) => (
                <TableRow key={index}>
                  {tableConfig.headers.map((headCell) => (
                    <TableCell key={`${headCell.id}-${index}`} align={headCell.align || 'left'} width={headCell.width}>
                      {renderCell?.[headCell.id as keyof PackageCreditsTableRow] ? (
                        renderCell[headCell.id as keyof PackageCreditsTableRow](
                          String(row[headCell.id as keyof PackageCreditsTableRow]),
                          row
                        )
                      ) : (
                        <Typography variant="body2">
                          {row[headCell.id as keyof PackageCreditsTableRow] || ''}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow
                sx={{
                  '& .MuiTableCell-footer': {
                    borderBottom: 0,
                  },
                }}
              >
                <StyledTablePagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={data?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </>
        </Table>
      </TableContainer>
    </Card>
  )
}
